import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Segment, Header, Icon, Button, Modal, Message,
    Image, Label, Dropdown, Form, Input, Loader, Popup, Grid,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../app/stores/store';
import { Game } from '../../../app/models/Game';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { GameVideoLink, VideoAccessLevel } from '../../../app/models/GameVideoLink';

// ─────────────────────────────────────────────────────────────────────────────
// Platform helpers
// ─────────────────────────────────────────────────────────────────────────────

interface PlatformInfo {
    name: string;
    embedUrl: string | null;      // null = platform can't be embedded
    thumbnailUrl: string | null;
    isEmbeddable: boolean;
    color: 'red' | 'blue' | 'purple' | 'grey';
    icon: 'youtube' | 'film' | 'video';
}

function getPlatformInfo(url: string): PlatformInfo {
    const fallback: PlatformInfo = {
        name: 'Video', embedUrl: null, thumbnailUrl: null,
        isEmbeddable: false, color: 'grey', icon: 'video',
    };

    try {
        const u    = new URL(url);
        const host = u.hostname.replace('www.', '');

        // ── YouTube (youtube.com/watch, youtu.be, /shorts/) ──────────────────
        if (host.includes('youtube.com') || host.includes('youtu.be')) {
            const videoId =
                u.searchParams.get('v') ??
                (host === 'youtu.be' ? u.pathname.slice(1).split('?')[0] : null) ??
                (u.pathname.startsWith('/shorts/') ? u.pathname.split('/shorts/')[1].split('?')[0] : null);

            if (!videoId) return { ...fallback, name: 'YouTube', icon: 'youtube', color: 'red' };

            const startParam = u.searchParams.get('t')
                ? `&start=${u.searchParams.get('t')!.replace(/s$/, '')}`
                : '';

            return {
                name: 'YouTube',
                // youtube-nocookie.com reduces cookie tracking
                embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1${startParam}`,
                thumbnailUrl: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
                isEmbeddable: true,
                color: 'red',
                icon: 'youtube',
            };
        }

        // ── Dailymotion ───────────────────────────────────────────────────────
        const dmMatch = url.match(/dailymotion\.com\/video\/([a-zA-Z0-9]+)/);
        if (dmMatch) {
            const dmId = dmMatch[1];
            return {
                name: 'Dailymotion',
                embedUrl: `https://www.dailymotion.com/embed/video/${dmId}?autoplay=0&queue-enable=false`,
                thumbnailUrl: `https://www.dailymotion.com/thumbnail/video/${dmId}`,
                isEmbeddable: true,
                color: 'blue',
                icon: 'film',
            };
        }

        // ── Vimeo ─────────────────────────────────────────────────────────────
        const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) {
            return {
                name: 'Vimeo',
                embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=0&title=1&byline=0`,
                thumbnailUrl: null,   // requires an extra API round-trip – skip for now
                isEmbeddable: true,
                color: 'blue',
                icon: 'film',
            };
        }

        // ── Twitch clips ──────────────────────────────────────────────────────
        const twitchClipMatch = url.match(/twitch\.tv\/\S+\/clip\/([a-zA-Z0-9_-]+)/);
        const clipSlug =
            twitchClipMatch?.[1] ??
            (host === 'clips.twitch.tv' ? u.pathname.slice(1) : null);
        if (clipSlug) {
            const parent = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
            return {
                name: 'Twitch',
                embedUrl: `https://clips.twitch.tv/embed?clip=${clipSlug}&parent=${parent}&autoplay=false`,
                thumbnailUrl: null,
                isEmbeddable: true,
                color: 'purple',
                icon: 'film',
            };
        }

        // ── Generic fallback ──────────────────────────────────────────────────
        return { ...fallback, name: host };
    } catch {
        return fallback;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// VideoPlayerModal – the in-app player
// ─────────────────────────────────────────────────────────────────────────────

interface VideoPlayerModalProps {
    link: GameVideoLink | null;
    onClose: () => void;
}

const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({ link, onClose }) => {
    const { t } = useTranslation();
    if (!link) return null;

    const platform = getPlatformInfo(link.videoLink);

    return (
        <Modal
            open={!!link}
            onClose={onClose}
            size="large"
            dimmer="blurring"
            closeIcon
            style={{ maxWidth: 920 }}
        >
            {/* Dark header bar */}
            <Modal.Header style={{
                backgroundColor: '#1a1a2e',
                color: '#fff',
                borderBottom: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 20px',
                // leave room for Semantic UI's built-in close (×) icon on the right
                paddingRight: 52,
            }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                    <Icon name={platform.icon} style={{ margin: 0 }} />
                    <span style={{ fontWeight: 700 }}>{platform.name}</span>
                    <span style={{ fontWeight: 400, fontSize: '0.8em', opacity: 0.65, overflow: 'hidden',
                                   textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        — {t('gameVideoLinks.player.addedBy', { name: link.uploaderFullName })}
                    </span>
                </span>

                {/* "Watch on YouTube" always visible in header */}
                <Button
                    as="a"
                    href={link.videoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    inverted
                    color={platform.color}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    style={{ flexShrink: 0 }}
                >
                    <Icon name="external alternate" />
                    {t('gameVideoLinks.player.watchOn', { platform: platform.name })}
                </Button>
            </Modal.Header>

            {/* Player area – black background */}
            <Modal.Content style={{ padding: 0, backgroundColor: '#000', lineHeight: 0 }}>
                {platform.isEmbeddable ? (
                    <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
                        <iframe
                            src={platform.embedUrl!}
                            title={`${platform.name} video player`}
                            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                            allowFullScreen
                            style={{
                                position: 'absolute',
                                top: 0, left: 0,
                                width: '100%', height: '100%',
                                border: 'none',
                            }}
                        />
                    </div>
                ) : (
                    /* Non-embeddable platform fallback */
                    <div style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        minHeight: 240, padding: '48px 24px', textAlign: 'center',
                        backgroundColor: '#1a1a2e', color: '#fff',
                    }}>
                        <Icon name="film" size="huge" style={{ marginBottom: 16, opacity: 0.55 }} />
                        <p style={{ fontSize: '1.05em', marginBottom: 20, opacity: 0.8 }}>
                            {t('gameVideoLinks.player.noEmbedMessage', { platform: platform.name })}
                        </p>
                        <Button
                            as="a"
                            href={link.videoLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            primary size="large"
                        >
                            <Icon name="external alternate" />
                            {t('gameVideoLinks.player.openExternal', { platform: platform.name })}
                        </Button>
                    </div>
                )}
            </Modal.Content>

            {/* Footer: uploader info left, action buttons right */}
            <Modal.Actions style={{
                backgroundColor: '#f9fafb',
                borderTop: '1px solid #d4d4d5',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 16px',
                flexWrap: 'wrap',
                gap: 8,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Image
                        avatar
                        src={link.uploaderImage || '/assets/user.png'}
                        style={{ width: 26, height: 26 }}
                    />
                    <span style={{ fontSize: '0.85em' }}>
                        <Link to={`/profiles/${link.uploaderUserID}`} onClick={onClose}>
                            {link.uploaderFullName}
                        </Link>
                        {link.createdAt && (
                            <span style={{ color: '#999', marginLeft: 8 }}>
                                {format(new Date(link.createdAt), 'dd MMM yyyy')}
                            </span>
                        )}
                    </span>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                        as="a"
                        href={link.videoLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        color={platform.color}
                    >
                        <Icon name="external alternate" />
                        {t('gameVideoLinks.player.watchOn', { platform: platform.name })}
                    </Button>
                    <Button basic size="small" onClick={onClose}>
                        {t('gameVideoLinks.player.close')}
                    </Button>
                </div>
            </Modal.Actions>
        </Modal>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// VideoCard
// ─────────────────────────────────────────────────────────────────────────────

interface VideoCardProps {
    link: GameVideoLink;
    onPlayClick: (link: GameVideoLink) => void;
    onDeleteClick: (id: string) => void;
    deletingId: string | null;
}

const VideoCard: React.FC<VideoCardProps> = ({ link, onPlayClick, onDeleteClick, deletingId }) => {
    const { t }                         = useTranslation();
    const [thumbError, setThumbError]   = useState(false);
    const platform                      = getPlatformInfo(link.videoLink);

    return (
        <div style={styles.card}>
            {/* ── Thumbnail click-to-play ─────────────────────────────────── */}
            <div
                role="button"
                tabIndex={0}
                style={styles.thumbnailWrapper}
                onClick={() => onPlayClick(link)}
                onKeyDown={e => e.key === 'Enter' && onPlayClick(link)}
                aria-label={t('gameVideoLinks.card.playAriaLabel', { platform: platform.name })}
                className="video-card-thumb"
            >
                {platform.thumbnailUrl && !thumbError ? (
                    <img
                        src={platform.thumbnailUrl}
                        alt={platform.name}
                        style={styles.thumbnail}
                        onError={() => setThumbError(true)}
                    />
                ) : (
                    <div style={styles.thumbnailPlaceholder}>
                        <Icon name={platform.icon} size="huge" style={{ color: '#555', margin: 0 }} />
                    </div>
                )}

                {/* Play / external overlay */}
                <div style={styles.playOverlay} className="play-overlay">
                    <div style={styles.playCircle}>
                        <Icon
                            name={platform.isEmbeddable ? 'play' : 'external alternate'}
                            size="large"
                            style={{ color: '#fff', marginLeft: platform.isEmbeddable ? 3 : 0, margin: 0 }}
                        />
                    </div>
                </div>
            </div>

            {/* ── Card body ──────────────────────────────────────────────── */}
            <div style={styles.cardBody}>
                {/* Platform + access badges */}
                <div style={styles.cardTopRow}>
                    <Label size="tiny" color={platform.color}>
                        <Icon name={platform.icon} />
                        {platform.name}
                    </Label>
                    <AccessBadge level={link.accessLimitedTo} />
                </div>

                {/* Action buttons */}
                <div style={styles.actionRow}>
                    {platform.isEmbeddable && (
                        <Button
                            size="mini" primary compact
                            icon="play" labelPosition="left"
                            content={t('gameVideoLinks.card.watchHere')}
                            onClick={() => onPlayClick(link)}
                        />
                    )}
                    <Button
                        as="a"
                        href={link.videoLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="mini" basic compact
                        color={platform.color}
                        icon="external alternate"
                        labelPosition="left"
                        content={t('gameVideoLinks.card.watchOn', { platform: platform.name })}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                </div>

                {/* Uploader row */}
                <div style={styles.uploaderRow}>
                    <Image
                        avatar
                        src={link.uploaderImage || '/assets/user.png'}
                        style={{ width: 20, height: 20, flexShrink: 0 }}
                    />
                    <span style={styles.uploaderName}>
                        <Link to={`/profiles/${link.uploaderUserID}`}>{link.uploaderFullName}</Link>
                    </span>
                    {link.createdAt && (
                        <span style={styles.uploadDate}>
                            {format(new Date(link.createdAt), 'dd MMM yyyy')}
                        </span>
                    )}
                </div>
            </div>

            {/* ── Delete button ───────────────────────────────────────────── */}
            {link.canDelete && (
                <div style={styles.deleteBtn}>
                    <Popup
                        content={t('gameVideoLinks.deleteTooltip')}
                        trigger={
                            <Button
                                icon="trash" size="mini" basic color="red"
                                loading={deletingId === link.id}
                                disabled={!!deletingId}
                                aria-label={t('gameVideoLinks.deleteAriaLabel')}
                                onClick={(e: React.MouseEvent) => { e.stopPropagation(); onDeleteClick(link.id); }}
                            />
                        }
                    />
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// AccessBadge
// ─────────────────────────────────────────────────────────────────────────────

const AccessBadge: React.FC<{ level: VideoAccessLevel }> = ({ level }) => {
    const { t } = useTranslation();
    const map: Record<VideoAccessLevel, { icon: 'users' | 'user' | 'globe'; color: 'teal' | 'orange' | 'green'; key: string }> = {
        [VideoAccessLevel.ParticipantsAndHost]: { icon: 'users', color: 'teal',   key: 'gameVideoLinks.access.participantsAndHost' },
        [VideoAccessLevel.UploaderOnly]:        { icon: 'user',  color: 'orange', key: 'gameVideoLinks.access.uploaderOnly'        },
        [VideoAccessLevel.Everyone]:            { icon: 'globe', color: 'green',  key: 'gameVideoLinks.access.everyone'            },
    };
    const cfg = map[level] ?? map[VideoAccessLevel.ParticipantsAndHost];
    return (
        <Label size="tiny" color={cfg.color}>
            <Icon name={cfg.icon} />{t(cfg.key)}
        </Label>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// AddLinkForm
// ─────────────────────────────────────────────────────────────────────────────

interface AddLinkFormProps { gameId: string; onClose: () => void; }

const AddLinkForm: React.FC<AddLinkFormProps> = observer(({ gameId, onClose }) => {
    const { t }                         = useTranslation();
    const { gameVideoLinkStore }        = useStore();
    const [url, setUrl]                 = useState('');
    const [access, setAccess]           = useState<VideoAccessLevel>(VideoAccessLevel.ParticipantsAndHost);
    const [urlError, setUrlError]       = useState('');
    const [preview, setPreview]         = useState<PlatformInfo | null>(null);

    const accessOptions = [
        { key: VideoAccessLevel.ParticipantsAndHost, value: VideoAccessLevel.ParticipantsAndHost,
          text: t('gameVideoLinks.access.participantsAndHost'), icon: 'users' },
        { key: VideoAccessLevel.UploaderOnly,        value: VideoAccessLevel.UploaderOnly,
          text: t('gameVideoLinks.access.uploaderOnly'),        icon: 'user'  },
        { key: VideoAccessLevel.Everyone,            value: VideoAccessLevel.Everyone,
          text: t('gameVideoLinks.access.everyone'),            icon: 'globe' },
    ];

    const handleUrlChange = (value: string) => {
        setUrl(value);
        if (urlError) setUrlError('');
        try {
            const info = getPlatformInfo(value.trim());
            setPreview(info.name !== 'Video' ? info : null);
        } catch { setPreview(null); }
    };

    const validate = (): boolean => {
        const t_ = url.trim();
        if (!t_) { setUrlError(t('gameVideoLinks.validation.urlRequired')); return false; }
        try {
            const u = new URL(t_);
            if (u.protocol !== 'https:' && u.protocol !== 'http:') throw new Error();
        } catch { setUrlError(t('gameVideoLinks.validation.urlInvalid')); return false; }
        setUrlError('');
        return true;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        const ok = await gameVideoLinkStore.addVideoLink({ gameID: gameId, videoLink: url.trim(), accessLimitedTo: access });
        if (ok) onClose();
    };

    return (
        <Form>
            <Form.Field error={!!urlError}>
                <label>{t('gameVideoLinks.form.urlLabel')}</label>
                <Input
                    placeholder={t('gameVideoLinks.form.urlPlaceholder')}
                    value={url}
                    onChange={(_, d) => handleUrlChange(d.value)}
                    fluid icon="linkify" iconPosition="left" autoFocus
                />
                {urlError && <span style={{ color: '#9f3a38', fontSize: '0.85em' }}>{urlError}</span>}
            </Form.Field>

            {/* Live platform detection preview */}
            {preview && (
                <Message positive style={{ padding: '8px 12px' }}>
                    <Icon name={preview.icon} />
                    {t('gameVideoLinks.form.platformDetected', { platform: preview.name })}
                    {preview.isEmbeddable && (
                        <span style={{ marginLeft: 6, fontSize: '0.85em', opacity: 0.8 }}>
                            — {t('gameVideoLinks.form.embedSupported')}
                        </span>
                    )}
                </Message>
            )}

            <Form.Field>
                <label>{t('gameVideoLinks.form.accessLabel')}</label>
                <Dropdown selection fluid options={accessOptions} value={access}
                          onChange={(_, d) => setAccess(d.value as VideoAccessLevel)} />
                <p style={{ color: '#767676', fontSize: '0.85em', marginTop: 4 }}>
                    {t(`gameVideoLinks.form.accessHint.${access}`)}
                </p>
            </Form.Field>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <Button basic content={t('gameVideoLinks.form.cancel')}
                        onClick={onClose} disabled={gameVideoLinkStore.submitting} />
                <Button primary content={t('gameVideoLinks.form.submit')}
                        onClick={handleSubmit}
                        loading={gameVideoLinkStore.submitting}
                        disabled={gameVideoLinkStore.submitting} />
            </div>
        </Form>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────────────────

interface Props { game: Game; }

export default observer(function GameVideoLinksPanel({ game }: Props) {
    const { t }                               = useTranslation();
    const { gameVideoLinkStore }              = useStore();
    const { videoLinks, loading, deletingId } = gameVideoLinkStore;

    const [addModalOpen,    setAddModalOpen]    = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [playerLink,      setPlayerLink]      = useState<GameVideoLink | null>(null);

    const canAdd = !!(game.isGoing || game.isHost);

    useEffect(() => {
        gameVideoLinkStore.loadVideoLinks(game.id);
        return () => gameVideoLinkStore.clearVideoLinks();
    }, [game.id, gameVideoLinkStore]);

    const handlePlayClick = useCallback((link: GameVideoLink) => {
        const info = getPlatformInfo(link.videoLink);
        if (info.isEmbeddable) {
            setPlayerLink(link);
        } else {
            window.open(link.videoLink, '_blank', 'noopener,noreferrer');
        }
    }, []);

    const handleDeleteClick    = useCallback((id: string) => setConfirmDeleteId(id), []);
    const handleDeleteConfirm  = async () => {
        if (!confirmDeleteId) return;
        await gameVideoLinkStore.deleteVideoLink(confirmDeleteId);
        setConfirmDeleteId(null);
    };

    return (
        <>
            {/* ── Header ───────────────────────────────────────────────────── */}
            <Segment textAlign="center" attached="top" inverted color="teal" style={{ border: 'none' }}>
                <Header>
                    <Icon name="film" />
                    {t('gameVideoLinks.title')}
                    {videoLinks.length > 0 && (
                        <Label circular size="tiny" color="orange"
                               style={{ marginLeft: 8, verticalAlign: 'middle' }}>
                            {videoLinks.length}
                        </Label>
                    )}
                </Header>
            </Segment>

            {/* ── Body ─────────────────────────────────────────────────────── */}
            <Segment attached clearing style={{ padding: '16px' }}>
                {canAdd && (
                    <div style={{ marginBottom: 16, textAlign: 'right' }}>
                        <Button primary size="small" icon="plus" labelPosition="left"
                                content={t('gameVideoLinks.addButton')}
                                onClick={() => setAddModalOpen(true)} />
                    </div>
                )}

                {loading && (
                    <div style={styles.emptyState}>
                        <Loader active inline="centered" size="medium" />
                    </div>
                )}

                {!loading && videoLinks.length === 0 && (
                    <div style={styles.emptyState}>
                        <Icon name="film" size="huge" color="grey" style={{ marginBottom: 12 }} />
                        <p style={{ color: '#767676', marginBottom: 0 }}>
                            {canAdd ? t('gameVideoLinks.empty.canAdd') : t('gameVideoLinks.empty.cannotAdd')}
                        </p>
                        {!canAdd && (
                            <Message info style={{ marginTop: 12, textAlign: 'left', maxWidth: 420, margin: '12px auto 0' }}>
                                <Icon name="info circle" />{t('gameVideoLinks.notParticipantNotice')}
                            </Message>
                        )}
                    </div>
                )}

                {!loading && videoLinks.length > 0 && (
                    <Grid stackable columns={2} style={{ margin: 0 }}>
                        {videoLinks.map(link => (
                            <Grid.Column key={link.id} style={{ padding: '6px 8px' }}>
                                <VideoCard
                                    link={link}
                                    onPlayClick={handlePlayClick}
                                    onDeleteClick={handleDeleteClick}
                                    deletingId={deletingId}
                                />
                            </Grid.Column>
                        ))}
                    </Grid>
                )}
            </Segment>

            {/* ── Video player modal ────────────────────────────────────────── */}
            <VideoPlayerModal link={playerLink} onClose={() => setPlayerLink(null)} />

            {/* ── Add video modal ───────────────────────────────────────────── */}
            <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)}
                   size="small" dimmer="blurring"
                   closeOnDimmerClick={!gameVideoLinkStore.submitting}>
                <Modal.Header style={{ backgroundColor: '#f8f8f9', borderBottom: '1px solid #d4d4d5' }}>
                    <Icon name="linkify" color="teal" />{t('gameVideoLinks.modal.addTitle')}
                </Modal.Header>
                <Modal.Content>
                    <Message info>
                        <Icon name="info circle" />{t('gameVideoLinks.modal.addInfo')}
                    </Message>
                    <AddLinkForm gameId={game.id} onClose={() => setAddModalOpen(false)} />
                </Modal.Content>
            </Modal>

            {/* ── Delete confirm modal ──────────────────────────────────────── */}
            <Modal open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}
                   size="mini" dimmer="blurring">
                <Modal.Header style={{ backgroundColor: '#f8f8f9', borderBottom: '1px solid #d4d4d5' }}>
                    {t('gameVideoLinks.deleteModal.title')}
                </Modal.Header>
                <Modal.Content><p>{t('gameVideoLinks.deleteModal.message')}</p></Modal.Content>
                <Modal.Actions style={{ backgroundColor: '#f9fafb', borderTop: '1px solid #d4d4d5' }}>
                    <Button basic content={t('gameVideoLinks.deleteModal.cancel')}
                            onClick={() => setConfirmDeleteId(null)} disabled={!!deletingId} />
                    <Button negative icon="trash" labelPosition="right"
                            content={t('gameVideoLinks.deleteModal.confirm')}
                            onClick={handleDeleteConfirm}
                            loading={!!deletingId} disabled={!!deletingId} />
                </Modal.Actions>
            </Modal>

            {/* Hover effect for thumbnail overlay */}
            <style>{`
                .video-card-thumb:hover .play-overlay,
                .video-card-thumb:focus .play-overlay {
                    background-color: rgba(0,0,0,0.45) !important;
                }
            `}</style>
        </>
    );
});

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
    card: {
        position: 'relative',
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    thumbnailWrapper: {
        position: 'relative',
        display: 'block',
        width: '100%',
        paddingTop: '52%',
        backgroundColor: '#1a1a2e',
        cursor: 'pointer',
        overflow: 'hidden',
        flexShrink: 0,
        outline: 'none',
    },
    thumbnail: {
        position: 'absolute',
        top: 0, left: 0,
        width: '100%', height: '100%',
        objectFit: 'cover',
    },
    thumbnailPlaceholder: {
        position: 'absolute',
        top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    playOverlay: {
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0)',
        transition: 'background-color 0.2s ease',
    },
    playCircle: {
        width: 52, height: 52,
        borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.65)',
        border: '2px solid rgba(255,255,255,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    cardBody: {
        padding: '10px 12px',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    cardTopRow: {
        display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center',
    },
    actionRow: {
        display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2,
    },
    uploaderRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 'auto',
        paddingTop: 6,
        borderTop: '1px solid #f0f0f0',
    },
    uploaderName: {
        fontSize: '0.82em', fontWeight: 600, flex: 1,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
    uploadDate: {
        fontSize: '0.75em', color: '#999', whiteSpace: 'nowrap', flexShrink: 0,
    },
    deleteBtn: {
        position: 'absolute', top: 6, right: 6, zIndex: 2,
    },
    emptyState: {
        textAlign: 'center', padding: '32px 16px', color: '#767676',
    },
};